(() => {
  if (customElements.get('nexvey-spark03-reels')) return;
  class SparkReels extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.track = this.querySelector('.nv-reels__track');
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.players = [...this.querySelectorAll('[data-inline-player]')];
      this.active = 0;
      this.motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.querySelector('[data-prev]').addEventListener('click', () => this.step(-1), opts);
      this.querySelector('[data-next]').addEventListener('click', () => this.step(1), opts);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.go(index), opts));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.sync());
      }, { ...opts, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || this.desktop()) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.step(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, opts);
      this.track.addEventListener('touchstart', event => {
        if (event.touches.length !== 1) { this.endDrag(); return; }
        const touch = event.touches[0];
        this.beginDrag(touch.clientX, touch.clientY);
      }, { ...opts, passive: true });
      this.track.addEventListener('touchmove', event => {
        if (event.touches.length !== 1) { this.endDrag(); return; }
        const touch = event.touches[0];
        this.moveDrag(touch.clientX, touch.clientY, event);
      }, { ...opts, passive: false });
      this.track.addEventListener('touchend', () => this.endDrag(), opts);
      this.track.addEventListener('touchcancel', () => this.endDrag(), opts);
      this.track.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('.nv-reels__player-controls')) return;
        this.beginDrag(event.clientX, event.clientY);
      }, opts);
      window.addEventListener('pointermove', event => {
        if (event.pointerType === 'mouse') this.moveDrag(event.clientX, event.clientY, event);
      }, opts);
      window.addEventListener('pointerup', event => { if (event.pointerType === 'mouse') this.endDrag(); }, opts);
      window.addEventListener('pointercancel', () => this.endDrag(), opts);
      window.addEventListener('blur', () => this.endDrag(), opts);
      this.track.addEventListener('dragstart', event => event.preventDefault(), opts);
      this.track.addEventListener('click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; }
      }, { ...opts, capture: true });
      this.querySelectorAll('[data-open]').forEach(button => button.addEventListener('click', () => this.start(button), opts));
      this.querySelectorAll('[data-toggle]').forEach(button => button.addEventListener('click', () => this.toggle(button), opts));
      this.querySelectorAll('[data-mute]').forEach(button => button.addEventListener('click', () => {
        const player = button.closest('[data-card]').querySelector('video');
        player.muted = !player.muted; this.updatePlayer(player);
      }, opts));
      this.players.forEach(player => {
        ['play', 'pause', 'ended', 'volumechange'].forEach(type => player.addEventListener(type, () => this.updatePlayer(player), opts));
        player.addEventListener('error', () => { player.closest('[data-card]').querySelector('.nv-reels__error').hidden = false; }, opts);
      });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pauseOthers(); }, opts);
      this.visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.intersectionRatio < 0.5) entry.target.querySelector('video')?.pause(); });
      }, { threshold: [0, 0.5] });
      this.cards.forEach(card => this.visibility.observe(card));
      this.observer = new ResizeObserver(() => { if (!this.drag) this.go(this.active, 'auto'); });
      this.observer.observe(this.track);
      this.go(0, 'auto');
    }
    maxScroll() { return Math.max(0, this.track.scrollWidth - this.track.clientWidth); }
    inset() { return parseFloat(getComputedStyle(this.track).paddingLeft) || 0; }
    desktop() { return matchMedia('(min-width:768px)').matches; }
    step(direction) {
      if (this.desktop()) return;
      if (!this.cards.length) return;
      this.pauseOthers();
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = this.cards[0].getBoundingClientRect().width + gap;
      this.track.scrollTo({ left: Math.max(0, Math.min(this.maxScroll(), this.track.scrollLeft + direction * distance)), behavior: this.motion() });
    }
    beginDrag(x, y) {
      if (this.desktop()) return;
      this.suppressClick = false;
      this.drag = { x, y, left: this.track.scrollLeft, axis: null };
    }
    moveDrag(x, y, event) {
      if (!this.drag) return;
      const dx = x - this.drag.x, dy = y - this.drag.y;
      if (!this.drag.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 6) this.drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (this.drag.axis !== 'x') return;
      this.pauseOthers();
      if (event.cancelable) event.preventDefault();
      this.track.classList.add('is-dragging');
      this.suppressClick = true;
      this.track.scrollLeft = Math.max(0, Math.min(this.maxScroll(), this.drag.left - dx));
    }
    endDrag() {
      if (!this.drag) return;
      const horizontal = this.drag.axis === 'x';
      this.drag = null;
      this.track.classList.remove('is-dragging');
      if (horizontal) { this.sync(); this.go(this.active); }
    }
    go(index, behavior = this.motion()) {
      if (this.desktop()) { this.track.scrollLeft = 0; this.active = 0; this.update(); return; }
      if (!this.cards.length) return;
      index = Math.max(0, Math.min(this.cards.length - 1, index));
      if (index !== this.active) this.pauseOthers();
      const rect = this.track.getBoundingClientRect();
      const card = this.cards[index].getBoundingClientRect();
      const left = this.track.scrollLeft + card.left - rect.left - this.inset();
      this.track.scrollTo({ left: Math.max(0, Math.min(this.maxScroll(), left)), behavior });
      this.active = index; this.update();
    }
    sync() {
      const previous = this.active;
      const rect = this.track.getBoundingClientRect();
      let closest = Infinity;
      this.cards.forEach((card, index) => {
        const r = card.getBoundingClientRect();
        const delta = Math.abs(r.left - rect.left - this.inset());
        if (delta < closest) { closest = delta; this.active = index; }
      });
      if (this.maxScroll() > 0 && this.track.scrollLeft >= this.maxScroll() - 1) this.active = this.cards.length - 1;
      if (previous !== this.active) this.pauseOthers();
      this.update();
    }
    update() {
      this.dots.forEach((dot, index) => dot.setAttribute('aria-pressed', String(index === this.active)));
      this.querySelector('[data-prev]').disabled = this.track.scrollLeft <= 1;
      this.querySelector('[data-next]').disabled = this.track.scrollLeft >= this.maxScroll() - 1;
    }
    pauseOthers(keep = null) {
      this.players?.forEach(player => { if (player !== keep) player.pause(); });
    }
    start(button) {
      if (!button.dataset.source) return;
      const card = button.closest('[data-card]'), player = card.querySelector('video');
      if (!player) return;
      this.pauseOthers(player);
      card.querySelector('.nv-reels__error').hidden = true;
      player.poster = button.querySelector('img')?.currentSrc || '';
      if (!player.getAttribute('src')) player.src = button.dataset.source;
      card.classList.add('is-started');
      card.querySelector('.nv-reels__player-controls').hidden = false;
      this.play(player);
      card.querySelector('[data-toggle]').focus({ preventScroll: true });
    }
    play(player) {
      this.pauseOthers(player);
      player.closest('[data-card]').querySelector('.nv-reels__error').hidden = true;
      player.play().catch(error => {
        if (error.name !== 'AbortError') player.closest('[data-card]').querySelector('.nv-reels__error').hidden = false;
        this.updatePlayer(player);
      });
    }
    toggle(button) {
      const player = button.closest('[data-card]').querySelector('video');
      if (player.paused || player.ended) this.play(player); else player.pause();
    }
    updatePlayer(player) {
      const card = player.closest('[data-card]');
      const toggle = card.querySelector('[data-toggle]'), mute = card.querySelector('[data-mute]');
      const state = player.paused || player.ended ? 'Play' : 'Pause';
      toggle.dataset.state = state.toLowerCase();
      toggle.setAttribute('aria-label', state + ' ' + card.querySelector('[data-open]').dataset.label);
      mute.dataset.state = player.muted ? 'muted' : 'sound';
      mute.setAttribute('aria-label', player.muted ? 'Turn sound on' : 'Turn sound off');
      mute.setAttribute('aria-pressed', String(!player.muted));
    }
    disconnectedCallback() {
      this.pauseOthers();
      this.drag = null; this.track?.classList.remove('is-dragging');
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame);
      this.visibility?.disconnect();
    }
  }
  customElements.define('nexvey-spark03-reels', SparkReels);
})();
